import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { StudyTypeIconDescriptor } from "../transformer";
import {
	accessErrorTypes,
	fileTypes,
	ICollaboratorData,
	IDropdownMaster,
	IIterationPayload,
	IMetaDetails,
	INotesPreviewData,
	INotesTemplate,
	ISelectedStudy,
	IStudyData,
	notesType,
	StudyPageTabType,
	StudyPageViewType,
	SupportedLanguageType,
	ITab,
	ILanguage,
	IFeatureList,
} from "../models/interfaces";
import { ITag } from "src/components/Searchbar/interface";

interface StudiesState {
	studiesData: IStudyData[];
	collaboratorData: ICollaboratorData[];
	dropdownMaster: IDropdownMaster[];
	searchText: string;
	selectedCollaborators: ITag[];
	metaDetails: IMetaDetails;
	userProfile: string;
	userProfileId: string;
	role: string;
	selectedStudy: ISelectedStudy;
	allStudyTypes: { label: string; display_name: string; key: string; value: string; icon: StudyTypeIconDescriptor | null; icon2?: StudyTypeIconDescriptor | null }[];
	selectedStudyType: { label: string; value: string; icon: StudyTypeIconDescriptor | null };
	allNotesTemplates: { id: string; name: notesType; template: INotesTemplate[] }[];
	selectedNotesType: { id: string; name: notesType; template: INotesTemplate[] };
	notesPreviewData: INotesPreviewData[];
	uploadedNotesId: string;
	saveColumnMappingPayload: {
		study_id: string;
		note_id: string;
		remapped_cols?: string[];
		column_remap?: boolean;
		file_id: string;
		column_mapping: {
			field_category: string;
			input_field_name: string;
			is_datetime: boolean;
			date_format?: string;
		}[];
		// start_date?: string;
		// end_date?: string;
	};
	uploadedNotesFiles: {
		name?: string;
		file_id?: string;
		note_id?: string;
		processing_status?: string;
		upload_details?: { filename: string };
	}[];
	isStudyTypeSelectionScreenVisible: boolean;
	availableLanguages: ILanguage[];
	supportedLanguages: string[];
	interfaceLanguage: ILanguage;
	isForbiddenModalOpen: boolean;
	isForbiddenMessage: string;
	accessErrorType: string;
	isNavDisabled: boolean;
	selectedStudyPageView: StudyPageViewType;
	selectedStudyPageTab: StudyPageTabType;
	addIteration: IIterationPayload[];
	isConfigDisabled: boolean;
	isViewInsightsDisabled: boolean;
	quant_tabs: ITab[];
	taxonomyDropdownData: any;
	folderDropdownData: any;
	selectedTableColumns: any;
	selectedTableData: any;
	selectedTableDateFormat: any;
	changesMadeInFiles: boolean;
	allWebBoardsTemplates: { id: string; name: notesType; template: INotesTemplate[] }[];
	isAllQuantFilesUploaded: boolean;
	isAllQuantConfigFilesUploaded: boolean;
	featureList: IFeatureList[];
	campaignVisibility?: boolean;
	isReflectiveModeSet?: boolean;
	isMappingUploaded?: boolean;
	// Composite (expiry + message) key the user dismissed the app-license-expiry banner for (or null).
	// Persisted via redux-persist (whitelisted in the store's studiesSlice transform) so the banner
	// stays hidden across reloads for the current window but reappears if the backend pushes a new
	// expiry time or a changed message. See appLicenseDismissKey in useAppLicenseStatus.
	appLicenseBannerDismissedFor: string | null;
}

export const initialState: StudiesState = {
	studiesData: [],
	collaboratorData: [],
	dropdownMaster: [],
	searchText: "",
	selectedCollaborators: [],
	metaDetails: {
		config: {
			is_report_available: {
				home: true,
				manage_study: false,
				add_study: true,
				ask_kural: true,
			},
		},
		id: "",
		fullName: "",
		user_email: "",
		isAdmin: false,
		roles: [],
		profiles: [],
		current_profile_id: "",
		current_primary_language: "",
		current_secondary_language: [],
		profile_language_mapping: [],
		feature_mapping: [],
		is_delete_executor: false,
	},

	userProfile: "",
	userProfileId: "",
	role: "",
	selectedStudy: {
		study_id: null,
		study_name: null,
		study_tags: [],
		study_collaborators: [],
		insights_id: null,
		access_type: null,
		created_by: null,
		study_type: null,
		insights_available: null,
	},
	allStudyTypes: [],
	selectedStudyType: null,
	allNotesTemplates: [],
	selectedNotesType: null,
	uploadedNotesId: null,
	notesPreviewData: [],
	uploadedNotesFiles: [],
	saveColumnMappingPayload: null,
	isStudyTypeSelectionScreenVisible: true,
	availableLanguages: [{ name: "English", code: SupportedLanguageType.English }],
	supportedLanguages: [SupportedLanguageType.English],
	interfaceLanguage: { name: "English", code: SupportedLanguageType.English },
	isForbiddenModalOpen: false,
	isForbiddenMessage: "",
	accessErrorType: "",
	isNavDisabled: false,
	selectedStudyPageView: StudyPageViewType.Grid,
	selectedStudyPageTab: StudyPageTabType.Recent,
	addIteration: [],
	isConfigDisabled: true,
	isViewInsightsDisabled: true,
	quant_tabs: [],
	taxonomyDropdownData: [],
	folderDropdownData: [],
	selectedTableColumns: {},
	selectedTableData: [],
	selectedTableDateFormat: "",
	changesMadeInFiles: false,
	allWebBoardsTemplates: [],
	isAllQuantFilesUploaded: false,
	isAllQuantConfigFilesUploaded: false,
	featureList: [],
	campaignVisibility: true,
	isReflectiveModeSet: false,
	isMappingUploaded: false,
	appLicenseBannerDismissedFor: null,
};

const studiesSlice = createSlice({
	name: "studies",
	initialState,
	reducers: {
		setAllStudiesData(state, action: PayloadAction<IStudyData[]>) {
			state.studiesData = action.payload;
		},
		setAllCollaboratorsData(state, action: PayloadAction<ICollaboratorData[]>) {
			state.collaboratorData = action.payload;
		},
		setDropdownMaster(state, action: PayloadAction<IDropdownMaster[]>) {
			state.dropdownMaster = action.payload;
		},
		setSearchText(state, action: PayloadAction<string>) {
			state.searchText = action.payload;
		},
		setSelectedCollaborators(state, action: PayloadAction<ITag[]>) {
			state.selectedCollaborators = action.payload;
		},
		updateStudyCollaboratorData(state, action: PayloadAction<{ collab_details: any; study_id: string }>) {
			const { collab_details, study_id } = action.payload;
			const study = state.studiesData.find((s) => s.study_id === study_id);
			study.collaborators = collab_details;
		},
		updateFileCount: (state, action: PayloadAction<{ study_id: string; study_type: string; count: number }>) => {
			const { count, study_id, study_type } = action.payload;
			const study = state.studiesData?.find((s) => s.study_id === study_id);
			if (study) {
				if (study_type === fileTypes.transcript) study.count_of_file = count;
				if (study_type === fileTypes.report) study.count_of_file = count;
				if (study_type === fileTypes.notes) study.count_of_file = count;
			}
		},
		setMetaDetails: (state, action: PayloadAction<IMetaDetails>) => {
			// state.metaDetails can be undefined after rehydrating a persisted state
			// that predates this field, so guard the fallback access.
			state.metaDetails = {
				...state.metaDetails,
				...action.payload,
				config: action.payload?.config ?? state.metaDetails?.config,
			};
		},
		setUserProfile: (state, action: PayloadAction<string>) => {
			state.userProfile = action.payload;
		},
		setUserProfileId: (state, action: PayloadAction<string>) => {
			state.userProfileId = action.payload;
		},
		setRole: (state, action: PayloadAction<string>) => {
			state.role = action.payload;
		},
		setSelectedStudy(state, action: PayloadAction<ISelectedStudy>) {
			state.selectedStudy = action.payload;
		},
		updateSelectedStudy(state, action: PayloadAction<any>) {
			state.selectedStudy = {
				...state.selectedStudy,
				...action.payload,
			};
		},
		setAllStudyTypes(state, action: PayloadAction<{ label: string; display_name: string; key: string; value: string; icon: StudyTypeIconDescriptor | null; icon2?: StudyTypeIconDescriptor | null; ask_kural_display_name: string }[]>) {
			state.allStudyTypes = action.payload;
		},
		setSelectedStudyType(state, action: PayloadAction<{ label: string; value: string; icon: StudyTypeIconDescriptor | null; key: string }>) {
			state.selectedStudyType = action.payload;
		},
		setAllNotesTemplates(state, action: PayloadAction<{ name: notesType; id: string; template: INotesTemplate[] }[]>) {
			state.allNotesTemplates = action.payload;
		},
		setSelectedNotesType(state, action: PayloadAction<{ id: string; name: notesType; template: INotesTemplate[] }>) {
			state.selectedNotesType = action.payload;
		},
		setNotesPreviewData(state, action: PayloadAction<INotesPreviewData[]>) {
			state.notesPreviewData = action.payload;
		},
		setUploadedNotesId(state, action: PayloadAction<string>) {
			state.uploadedNotesId = action.payload;
		},
		setUploadedNotesFiles(
			state,
			action: PayloadAction<
				{
					name?: string;
					file_id?: string;
					note_id?: string;
					processing_status?: string;
					display_on_ui?: string;
					upload_details?: { filename: string };
				}[]
			>
		) {
			state.uploadedNotesFiles = action.payload;
		},
		updateSaveColumnMappingPayload(state, action: PayloadAction<Partial<typeof state.saveColumnMappingPayload>>) {
			state.saveColumnMappingPayload = {
				...state.saveColumnMappingPayload,
				...action.payload,
			};
		},
		setIsStudyTypeSelectionScreenVisible(state, action: PayloadAction<boolean>) {
			state.isStudyTypeSelectionScreenVisible = action.payload;
		},
		setAvailableLanguages(state, action: PayloadAction<ILanguage[]>) {
			state.availableLanguages = action.payload;
		},
		setSupportedLanguages(state, action: PayloadAction<string[]>) {
			state.supportedLanguages = action.payload;
		},
		setInterfaceLanguage(state, action: PayloadAction<ILanguage>) {
			state.interfaceLanguage = action.payload;
		},
		openForbiddenModal: (state, action: PayloadAction<{ message: string; type: string }>) => {
			const { message, type } = action.payload;
			state.isForbiddenModalOpen = true;
			state.isForbiddenMessage = message;
			state.accessErrorType = type;
			state.isNavDisabled =
				type === accessErrorTypes.NO_ROLES ||
				type === accessErrorTypes.NO_PROFILES ||
				type === accessErrorTypes.ADMIN_ROLE_NO_PROFILE ||
				type === accessErrorTypes.PROFILE_REVOKED ||
				type === accessErrorTypes.ADMIN_REVOKED ||
				type === accessErrorTypes.ACCOUNT_DISABLED ||
				type === accessErrorTypes.ACCESS_EXPIRED ||
				type === accessErrorTypes.APP_ACCESS_EXPIRED;
		},
		closeForbiddenModal: (state) => {
			state.isForbiddenModalOpen = false;
		},
		setAppLicenseBannerDismissedFor: (state, action: PayloadAction<string | null>) => {
			state.appLicenseBannerDismissedFor = action.payload;
		},
		setIsNavDisabled: (state, action: PayloadAction<boolean>) => {
			state.isNavDisabled = action.payload;
		},
		setSelectedStudyPageView(state, action: PayloadAction<StudyPageViewType>) {
			state.selectedStudyPageView = action.payload;
		},
		setSelectedStudyPageTab(state, action: PayloadAction<StudyPageTabType>) {
			state.selectedStudyPageTab = action.payload;
		},
		setAddIteration(state, action: PayloadAction<IIterationPayload[]>) {
			state.addIteration = action.payload;
		},
		updateStudyStar(state, action: PayloadAction<{ study_id: string; star: boolean }>) {
			const { star, study_id } = action.payload;
			const study = state.studiesData.find((s) => s.study_id === study_id);
			if (study) {
				study.is_star = star;
			}
		},
		setIsConfigDisabled(state, action: PayloadAction<boolean>) {
			state.isConfigDisabled = action.payload;
		},
		setIsViewInsightsDisabled(state, action: PayloadAction<boolean>) {
			state.isViewInsightsDisabled = action.payload;
		},
		setTaxonomyDropdownData(state, action: PayloadAction<any>) {
			state.taxonomyDropdownData = action.payload;
		},
		setFolderDropdownData(state, action: PayloadAction<any>) {
			state.folderDropdownData = action.payload;
		},
		setSelectedTableColumns(state, action: PayloadAction<any>) {
			state.selectedTableColumns = action.payload;
		},
		setSelectedTableData(state, action: PayloadAction<any>) {
			state.selectedTableData = action.payload;
		},
		setSelectedTableDateFormat(state, action: PayloadAction<any>) {
			state.selectedTableDateFormat = action.payload;
		},
		setChangesMadeInFiles(state, action: PayloadAction<boolean>) {
			state.changesMadeInFiles = action.payload;
		},
		setAllWebBoardsTemplates(state, action: PayloadAction<{ name: notesType; id: string; template: INotesTemplate[] }[]>) {
			state.allWebBoardsTemplates = action.payload;
		},
		setQuantFilesUploaded(state, action: PayloadAction<any>) {
			const requiredTypesForQuantFiles = [fileTypes.survey, fileTypes.rawData, fileTypes.adm];
			const requiredTypesForQuantConfigFiles = [fileTypes.analysisConfig, fileTypes.chartConfig];
			const quantFilesMap = new Map<string, boolean>();
			const quantConfigFilesMap = new Map<string, boolean>();

			for (const file of action.payload) {
				const { document_type, isTemplate } = file;

				// Only add if not a template and document_type is in the requiredTypesForQuantFiles list
				if (!isTemplate && requiredTypesForQuantFiles.includes(document_type)) {
					quantFilesMap.set(document_type, true);
				}

				// Only add if not a template and document_type is in the requiredTypesForQuantConfigFiles list
				if (!isTemplate && requiredTypesForQuantConfigFiles.includes(document_type)) {
					quantConfigFilesMap.set(document_type, true);
				}
			}

			state.isAllQuantFilesUploaded = requiredTypesForQuantFiles.every((type) => quantFilesMap.has(type));
			state.isAllQuantConfigFilesUploaded = requiredTypesForQuantConfigFiles.every((type) => quantConfigFilesMap.has(type));
		},
		setFeatureList: (state, action: PayloadAction<IFeatureList[]>) => {
			state.featureList = action.payload;
		},
		updateFeatureList: (state, action: PayloadAction<IFeatureList>) => {
			const featureIndex = state.featureList.findIndex((feature) => feature.feature_name === action.payload.feature_name);
			if (featureIndex !== -1) {
				state.featureList[featureIndex] = action.payload;
			}
		},
		setCampaignVisibility: (state, action: PayloadAction<boolean>) => {
			state.campaignVisibility = action.payload;
		},
		setReflectiveModeToSet: (state, action: PayloadAction<boolean>) => {
			state.isReflectiveModeSet = action.payload;
		},
		updateStudyList: (state, action: PayloadAction<{ study_id: string; changes: Partial<IStudyData> }>) => {
			const { study_id, changes } = action.payload;
			state.studiesData = state.studiesData.map((s) =>
				s.study_id === study_id ? { ...s, ...changes } : s
			);
		},
		setIsMappingUploaded: (state, action: PayloadAction<boolean>) => {
			state.isMappingUploaded = action.payload;
		},
	},
});

export const {
	closeForbiddenModal,
	openForbiddenModal,
	setAddIteration,
	setAllCollaboratorsData,
	setAllNotesTemplates,
	setAllStudiesData,
	setAllStudyTypes,
	setAllWebBoardsTemplates,
	setAppLicenseBannerDismissedFor,
	setAvailableLanguages,
	setChangesMadeInFiles,
	setDropdownMaster,
	setInterfaceLanguage,
	setIsConfigDisabled,
	setIsNavDisabled,
	setIsStudyTypeSelectionScreenVisible,
	setMetaDetails,
	setNotesPreviewData,
	setRole,
	setSearchText,
	setSelectedCollaborators,
	setSelectedNotesType,
	setSelectedStudy,
	setSelectedStudyPageTab,
	setSelectedStudyPageView,
	setSelectedStudyType,
	setSelectedTableColumns,
	setSelectedTableData,
	setSelectedTableDateFormat,
	setSupportedLanguages,
	setTaxonomyDropdownData,
	setFolderDropdownData,
	setUploadedNotesFiles,
	setUploadedNotesId,
	setUserProfile,
	setUserProfileId,
	updateFileCount,
	updateSaveColumnMappingPayload,
	updateSelectedStudy,
	updateStudyCollaboratorData,
	updateStudyStar,
	setIsViewInsightsDisabled,
	setQuantFilesUploaded,
	setFeatureList,
	updateFeatureList,
	setCampaignVisibility,
	setReflectiveModeToSet,
	updateStudyList,
	setIsMappingUploaded,
} = studiesSlice.actions;

export default studiesSlice.reducer;
